// src/components/Dashboard/AdminApproved.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import ApplicationModal from './ApplicationModal';

const AdminApproved = () => {
  const [approvedApplications, setApprovedApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchApprovedApplications = async () => {
      try {
        // Fetch approved member applications
        const memberQuerySnapshot = await getDocs(collection(db, 'memberApplications'));
        const memberApps = [];
        memberQuerySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.status === 'approved') {
            const mappedData = {
              id: doc.id, 
              type: 'member', 
              name: data.fullNames || data.fullName || 'N/A', 
              fullNames: data.fullNames || data.fullName || 'N/A',
              personalEmail: data.personalEmailAddress || data.personalEmail || data.email || 'N/A',
              submittedAt: data.submittedAt || data.createdAt || data.dateSubmitted || null,
              approverTitleFullNames: data.approverTitleFullNames || data.approverName || 'N/A',
              approverDate: data.approverDate || data.approvedAt || data.approvalDate || data.approvalTimestamp || null,
              ...data 
            };
            
            // Handle date formatting for submittedAt
            if (mappedData.submittedAt) {
              if (mappedData.submittedAt.seconds !== undefined) {
                mappedData.submittedAt = new Date(mappedData.submittedAt.seconds * 1000).toISOString();
              } else if (mappedData.submittedAt.toDate) {
                mappedData.submittedAt = mappedData.submittedAt.toDate().toISOString();
              }
            }
            
            // Handle date formatting for approverDate
            if (mappedData.approverDate) {
              if (mappedData.approverDate.seconds !== undefined) {
                mappedData.approverDate = new Date(mappedData.approverDate.seconds * 1000).toISOString();
              } else if (mappedData.approverDate.toDate) {
                mappedData.approverDate = mappedData.approverDate.toDate().toISOString();
              }
            }
            
            memberApps.push(mappedData);
          }
        });

        // Fetch approved partner applications
        const partnerQuerySnapshot = await getDocs(collection(db, 'partnerApplications'));
        const partnerApps = [];
        partnerQuerySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.status === 'approved') {
            const mappedData = {
              id: doc.id, 
              type: 'partner', 
              name: data.entityRegisteredName || data.tradingName || data.businessName || 'N/A', 
              entityRegisteredName: data.entityRegisteredName || data.businessName || 'N/A',
              tradingName: data.tradingName || data.businessName || 'N/A',
              businessEmail: data.businessEmail || data.email || 'N/A',
              submittedAt: data.submittedAt || data.createdAt || null,
              approverTitleFullNames: data.approverTitleFullNames || data.approverName || 'N/A',
              approverDate: data.approverDate || data.approvedAt || data.approvalDate || data.approvalTimestamp || null,
              ...data 
            };
            
            // Handle date formatting for submittedAt
            if (mappedData.submittedAt) {
              if (mappedData.submittedAt.seconds !== undefined) {
                mappedData.submittedAt = new Date(mappedData.submittedAt.seconds * 1000).toISOString();
              } else if (mappedData.submittedAt.toDate) {
                mappedData.submittedAt = mappedData.submittedAt.toDate().toISOString();
              }
            }
            
            // Handle date formatting for approverDate
            if (mappedData.approverDate) {
              if (mappedData.approverDate.seconds !== undefined) {
                mappedData.approverDate = new Date(mappedData.approverDate.seconds * 1000).toISOString();
              } else if (mappedData.approverDate.toDate) {
                mappedData.approverDate = mappedData.approverDate.toDate().toISOString();
              }
            }
            
            partnerApps.push(mappedData);
          }
        });

        // Combine and sort by approval date (newest first)
        const allApproved = [...memberApps, ...partnerApps].sort((a, b) => {
          const dateA = a.approverDate ? new Date(a.approverDate).getTime() : 
                       a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
          const dateB = b.approverDate ? new Date(b.approverDate).getTime() : 
                       b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
          return dateB - dateA;
        });

        setApprovedApplications(allApproved);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching approved applications:', error);
        setLoading(false);
      }
    };

    fetchApprovedApplications();
  }, []);

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
  };

  if (loading) {
    return <div className="loading">Loading approved applications...</div>;
  }

  return (
    <div className="dashboard-section">
      <h3>Approved Applications ({approvedApplications.length})</h3>

      <div className="table-container">
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Approval Date</th>
              <th>Approver</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {approvedApplications.map(application => {
              // Format approval date
              let approvalDate = 'N/A';
              if (application.approverDate) {
                approvalDate = new Date(application.approverDate).toLocaleDateString();
              } else if (application.submittedAt) {
                approvalDate = new Date(application.submittedAt).toLocaleDateString();
              }

              return (
                <tr key={`${application.type}-${application.id}`}>
                  <td>
                    <span className={`type-badge type-${application.type}`}>
                      {application.type === 'member' ? 'Member' : 'Partner'}
                    </span>
                  </td>
                  <td>
                    {application.type === 'member' 
                      ? application.fullNames || 'N/A'
                      : application.entityRegisteredName || application.tradingName || 'N/A'
                    }
                  </td>
                  <td>
                    {application.type === 'member' 
                      ? application.personalEmail || 'N/A'
                      : application.businessEmail || 'N/A'
                    }
                  </td>
                  <td>{approvalDate}</td>
                  <td>{application.approverTitleFullNames || 'N/A'}</td>
                  <td>
                    <button
                      className="btn-primary"
                      onClick={() => viewApplicationDetails(application)}
                    >
                      View Details
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {approvedApplications.length === 0 && (
          <p className="no-data">No approved applications found.</p>
        )}
      </div>

      {showModal && selectedApplication && (
        <ApplicationModal 
          application={selectedApplication} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
};

export default AdminApproved;